import { Button } from '@/components/buttons';
import Image from 'next/image';
import {
  ArrowRight,
  ArrowUpRight,
  Box,
  CircleDollarSign,
  Copy,
  PieChart,
  Radar,
  Store,
  Users,
} from 'lucide-react';
import Carousel from '@/components/card/Carousel';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import Accordion from '@/components/accordion/accordion.component';
import { PUBLIC_FAQ_LIST } from '@/_shared/constants/public-faq.constant';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import Loader from '@/components/loaders/loader.component';
import HomeWrapper from '@/components/pages/HomeSection';
import { motion, AnimatePresence } from 'framer-motion';

interface HeroProps {
  title: string;
  name: string;
  desc: string;
  imageSrc: string;
}

const HeroSection = ({ desc, imageSrc, name, title }: HeroProps) => {
  return (
    <div className="flex flex-col gap-y-8 items-center">
      <div className="flex flex-col items-center xl:max-w-[840px]">
        <div className="text-center">
          <span className="text-center text-2xl md:text-4xl font-bold">
            {title}{' '}
          </span>
          <span className="text-main text-center text-2xl md:text-4xl font-bold text-nowrap">
            {name}
          </span>
        </div>
        <div className="font-medium text-center mt-4 lg:text-xl">{desc}</div>
      </div>
      <div className="pt-4 px-4 md:pt-[60px] md:px-[60px] bg-[#F4F3EC] rounded-lg">
        <Image src={imageSrc} width={1000} height={500} alt={''} />
      </div>
    </div>
  );
};

const RenderTooltipSection = () => {
  const [clicked, setClicked] = useState(false);
  return (
    <div className="hs-tooltip inline-block">
      <button
        className="hs-tooltip-toggle w-full flex gap-4 items-center justify-center hover:underline"
        onMouseLeave={() => setClicked(false)}
        onClick={() => {
          setClicked(true);
          navigator.clipboard.writeText('metercube-support@cube.asia');
        }}
      >
        <span className="text-lg lg:text-2xl font-medium">
          metercube-support@cube.asia
        </span>
        <Copy className="size-6 text-white" />
        <span
          className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-sm font-medium text-white rounded-md shadow-2xs dark:bg-neutral-700"
          role="tooltip"
        >
          {clicked ? 'Email copied!' : 'Copy to clipboard'}
        </span>
      </button>
    </div>
  );
};
const Home = () => {
  const [initLoading, setInitLoading] = useState(true);
  const { data: session } = useSession();
  const router = useRouter();
  useEffect(() => {
    if (
      session?.user.status === 'complete' &&
      session?.user.consent !== 'false'
    ) {
      router.replace('/hub');
    } else {
      setInitLoading(false);
    }
  }, [router, session?.user]);

  const scrollTo = (key: string) => {
    const section = sections.find((s) => s.key === key);
    section?.ref.current?.scrollIntoView({ behavior: 'smooth' });
    setActiveCat(key);
  };

  const cubeRef = useRef<HTMLDivElement | null>(null);
  const categoriesRef = useRef<HTMLDivElement | null>(null);
  const countriesRef = useRef<HTMLDivElement | null>(null);
  const platformsRef = useRef<HTMLDivElement | null>(null);

  const sections = [
    { key: 'Cube principle', ref: cubeRef },
    { key: 'Categories', ref: categoriesRef },
    { key: 'Countries', ref: countriesRef },
    { key: 'Platforms', ref: platformsRef },
  ];

  const [activeCat, setActiveCat] = useState('Categories');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = sections.find(
              (s) => s.ref.current === entry.target
            );
            if (section) {
              setActiveCat(section.key);
            }
          }
        });
      },
      { root: null, threshold: 1 }
    );

    sections.forEach((s) => {
      if (s.ref.current) {
        observer.observe(s.ref.current);
      }
    });

    return () => {
      sections.forEach((s) => {
        if (s.ref.current) observer.unobserve(s.ref.current);
      });
    };
  }, [sections.map((s) => s.ref.current).join(',')]);

  const usecases = [
    {
      icon: <PieChart className="text-white size-6" />,
      title: 'Category & Market Sizing',
      desc: 'Quickly size e-commerce categories, track growth trends, and identify emerging opportunities across platforms.',
    },
    {
      icon: <Box className="text-white size-6" />,
      title: 'Competitive Benchmarking',
      desc: 'Compare brand and product performance against key competitors with granular sales and market share data.',
    },
    {
      icon: <Radar className="text-white size-6" />,
      title: 'Portfolio Performance Tracking',
      desc: 'Monitor how your brands, SKUs, and campaigns are performing across e-commerce channels — with clear, actionable insights.',
    },
    {
      icon: <Users className="text-white size-6" />,
      title: 'Consumer Behavior Analysis',
      desc: 'Understand shifts in online purchasing behavior, category seasonality, and emerging trends to optimize marketing strategies.',
    },
    {
      icon: <Store className="text-white size-6" />,
      title: 'Market Entry & Expansion',
      desc: 'Use data-driven insights to guide product launches, market entry strategies, and regional expansion decisions.',
    },
    {
      icon: <CircleDollarSign className="text-white size-6" />,
      title: 'Investment Due Diligence',
      desc: 'Equip investment teams with reliable e-commerce data to assess market dynamics, evaluate target companies, and identify growth potential.',
    },
  ];

  // const [activeHero, setActiveHero] = useState('category');
  // const activeHeroClass = 'text-blue-800 bg-blue-100 border-blue-100';
  const heroVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <>
      {initLoading ? (
        <div className="flex items-center justify-center mt-40">
          <Loader />
        </div>
      ) : (
        <div>
          <HomeWrapper className="bg-background-secondary overflow-hidden">
            <div className="grid grid-cols-12 !md:gap-4">
              <div className="flex flex-col gap-8 z-20 pr-4 lg:pr-0 col-span-12 md:col-span-9 lg:col-span-7 justify-center">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold lg:leading-snug">
                  A clearer picture of
                  <br />
                  <span className="text-main">e-commerce performance</span>,
                  <br />
                  delivered with precision
                </div>
                <div className="text-sm md:text-xl">
                  Gain a comprehensive view of your market through
                  high-granularity data and expertly curated reports — built for
                  decision-makers in a fast-moving digital world.
                </div>
                <div className="flex gap-4 w-full">
                  <a href="#explore">
                    <Button>Explore Platform</Button>
                  </a>
                  <Link href={'/login'}>
                    <Button variant="white" iconEnd={<ArrowRight />}>
                      Go to Metercube
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block lg:col-span-1" />
              <div className="hidden md:block col-span-3 lg:col-span-4 relative">
                <div className="overflow-visible">
                  <Image
                    src="/assets/public-metercube/home-banner.jpg"
                    alt={''}
                    width={1000}
                    height={500}
                    className="max-w-[unset] w-[800px] h-[500px] aspect-[16/9] rounded-lg shadow"
                  />
                </div>
              </div>
            </div>
          </HomeWrapper>
          <div className="pb-12 md:pb-20">
            <div className="bg-background-secondary ">
              <div className="flex flex-col items-center justify-center">
                <div>Partnered with</div>
                <Carousel />
              </div>
            </div>
            <div className="absolute left-0 w-full overflow-hidden leading-[0]">
              <div className="relative h-8 md:h-20">
                <div className="absolute top-0 w-full h-full bg-background-secondary [clip-path:ellipse(50%_100%_at_50%_0%)]"></div>
              </div>
            </div>
          </div>
          <HomeWrapper id="explore" className="scroll-m-24">
            {/* Animated content */}
            <AnimatePresence mode="wait">
              <motion.div
                key="category"
                variants={heroVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <HeroSection
                  title="Understand exactly what’s driving growth with"
                  name="Category Tracking"
                  desc="Unlock the full potential of your product data with our powerful Category tracking feature. Automatically classify products into accurate, granular categories using AI-driven tagging."
                  imageSrc="/assets/public-metercube/category-tracking.png"
                />
              </motion.div>
            </AnimatePresence>
          </HomeWrapper>
          <HomeWrapper className="bg-background-secondary">
            <div className="flex flex-col items-center">
              <div className="text-2xl md:text-4xl font-bold mb-4">
                Use cases
              </div>
              <div className="md:text-xl mb-12 text-center">
                MeterCube helps e-commerce and insight professionals turn
                complex market data into clear, actionable intelligence
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
                {usecases.map((u, idx) => (
                  <div key={idx} className="shadow bg-white rounded-xl p-4">
                    <div className="bg-main w-fit p-3 rounded-lg mb-4">
                      {u.icon}
                    </div>
                    <div className="font-bold text-lg md:text-xl mb-2">
                      {u.title}
                    </div>
                    <div className="font-medium text-sm md:text-base">
                      {u.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </HomeWrapper>
          <HomeWrapper className="bg-cube text-white overflow-clip">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-8">
              <div className="col-span-3 md:col-span-1 px-0 md:pl-4 xl:pl-8 md:pb-20">
                <div className="flex flex-col gap-4 sticky top-24">
                  {[
                    'Cube principle',
                    'Categories',
                    'Countries',
                    'Platforms',
                  ].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => scrollTo(cat)}
                      className={`uppercase text-left text-sm font-medium py-2 px-3 border-b ${
                        activeCat === cat
                          ? 'text-white border-b-white'
                          : 'text-global-text-secondary border-b-global-text-secondary'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="col-span-3 flex flex-col gap-8">
                <div
                  className="border border-white p-8 scroll-m-20 relative"
                  id="Cube principle"
                  ref={cubeRef}
                >
                  <div className="md:text-xl font-light mb-8 uppercase">
                    Cube principle
                  </div>
                  <div className="flex flex-col gap-y-4">
                    <div className="text-3xl md:text-6xl md:leading-snug font-medium ">
                      How MeterCube is organized
                    </div>
                    <div className="text-base font-medium">
                      MeterCube structures e-commerce data across Categories,
                      Countries, and Platforms, giving you a clear and
                      consistent view of products, markets, and channels for
                      smarter decision-making.
                    </div>
                  </div>
                </div>

                <div
                  className="border border-white p-8 scroll-m-20 relative"
                  id="Categories"
                  ref={categoriesRef}
                >
                  <div className="absolute top-0 right-0 w-[126px] sm:w-[39%] md:w-[200px] xl:w-[256px]">
                    <Image
                      src="/assets/public-metercube/MeterCube1Animate.gif"
                      width={500}
                      height={500}
                      alt="gif1"
                    />
                  </div>
                  <div className="md:text-xl font-light mb-[126px] sm:mb-[180px] md:mb-[120px] uppercase">
                    Categories
                  </div>
                  <div className="flex flex-col gap-y-4">
                    <div className="text-3xl md:text-4xl lg:text-6xl lg:leading-snug font-medium">
                      Coverage
                      <br className="hidden sm:block" /> of all physical
                      <br className="hidden sm:block" /> goods sold online
                    </div>
                    <div className="text-base font-medium">
                      Cube provides market data and insights for all trade
                      categories in e-commerce. Whether you are selling skin
                      care or pet food, we have the insights to fast-track your
                      online growth.
                    </div>
                  </div>
                </div>

                <div
                  className="border border-white p-8 scroll-m-20 relative"
                  id="Countries"
                  ref={countriesRef}
                >
                  <div className="absolute bottom-0 left-[calc(50%-140px)] w-[280px] lg:left-[calc(50%-200px)] lg:w-[400px]">
                    <Image
                      src="/assets/public-metercube/MeterCube2Animate.gif"
                      width={500}
                      height={500}
                      alt="gif1"
                    />
                  </div>
                  <div className="md:text-xl font-light mb-3 uppercase">
                    Countries
                  </div>
                  <div className="flex flex-col gap-y-4 mb-[180px] lg:mb-[240px] xl:mb-[300px]">
                    <div className="text-3xl md:text-4xl lg:text-6xl lg:leading-snug font-medium">
                      Detailed and reliable data points for your target markets
                    </div>
                    <div className="text-base font-medium">
                      Cube’s coverage started in Southeast Asia, but now covers
                      other growth markets like Brazil and Taiwan, too. Contact
                      us for more information.
                    </div>
                  </div>
                </div>
                <div
                  className="border border-white p-8 text-end scroll-m-20 relative"
                  id="Platforms"
                  ref={platformsRef}
                >
                  <div className="absolute top-0 left-0 w-[126px] sm:w-[25%] md:w-[200px] lg:w-[256px]">
                    <Image
                      src="/assets/public-metercube/MeterCube3Animate.gif"
                      width={500}
                      height={500}
                      alt="gif1"
                    />
                  </div>
                  <div className="md:text-xl font-light mb-[126px] lg:mb-[140px] uppercase">
                    Platforms
                  </div>
                  <div className="flex flex-col gap-y-4">
                    <div className="text-3xl md:text-4xl lg:text-6xl lg:leading-snug font-medium">
                      Full coverage <br className="hidden md:block" /> of
                      important{' '}
                      <span className="xs:text-nowrap">
                        e-commerce platforms
                      </span>
                    </div>
                    <div className="text-base font-medium">
                      We provide uniform coverage for all the e-commerce
                      platforms that matter. Unified categories, tags, and
                      insights give you intelligence about the whole market.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </HomeWrapper>
          <HomeWrapper>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex flex-col gap-y-4 col-span-1">
                <div className="text-2xl md:text-4xl font-bold">
                  Frequently Asked Question
                </div>
                <div className="md:text-xl">
                  Find answers to frequently asked questions about MeterCube and
                  Cube.
                </div>
              </div>
              <div className="col-span-1">
                <Accordion items={PUBLIC_FAQ_LIST} />
              </div>
            </div>
          </HomeWrapper>
          <HomeWrapper className="home-gradient">
            <div className="bg-main py-8 px-4 md:py-16 md:px-8 text-center rounded-2xl text-white">
              <div className="text-2xl md:text-4xl font-bold mb-4">
                Navigate the e-commerce market with confidence
              </div>
              <div className="md:text-xl mb-8">
                Let us help you streamline your approach, refine your tactics,
                and uncover new opportunities for growth. We’ll guide you toward
                smarter decisions and lasting success. Please contact us at
              </div>
              <RenderTooltipSection />
            </div>
          </HomeWrapper>
          <HomeWrapper className="bg-cube">
            <div className="grid grid-cols-12 text-white gap-y-8">
              <div className="flex-shrink max-w-[300px] md:col-span-5 lg:col-span-4 col-span-12">
                <Image
                  src="/assets/MeterCube_Logo_Primary_Ligh.svg"
                  alt={''}
                  width={1000}
                  height={500}
                  className="w-[200px] mb-4"
                />
                <div className="text-sm font-medium text-wrap">
                  Empowered your decision making with MeterCube services
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:col-span-5 lg:col-span-4 col-span-12 gap-2">
                <Link href="https://cube.asia" target="_blank">
                  <div className="flex items-center gap-1">
                    Cube.asia <ArrowUpRight />
                  </div>
                </Link>
                <Link href="https://cube.asia/security" target="_blank">
                  <div className="flex items-center gap-1">
                    Security <ArrowUpRight />
                  </div>
                </Link>
                <Link href="https://trust.cube.asia" target="_blank">
                  <div className="flex items-center gap-1">
                    Trust Center <ArrowUpRight />
                  </div>
                </Link>
                <Link href="/privacy-policy">Privacy policy</Link>
                <Link href="/acceptable-use-policy">Acceptable Use Policy</Link>
              </div>
              <div className="md:col-span-2 lg:col-span-4 col-span-12 flex md:justify-end">
                {/* www.aicpa.org/soc4so */}
                <Link href="https://aicpa.org/soc4so" target="_blank">
                  <Image
                    src="/assets/soc.png"
                    alt={''}
                    width={80}
                    height={80}
                    className="w-[80px] mb-4"
                    quality={90}
                  />
                </Link>
              </div>
            </div>
            <hr className="text-white my-8" />
            <div className="text-white text-xs font-medium text-center w-full">
              Copyright 2025 MeterCube. All Rights Reserved.
            </div>
          </HomeWrapper>
        </div>
      )}
    </>
  );
};

Home.defaultProps = {
  hideScaffold: true,
};

export default Home;
